import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { useEcommerceContext, EcommerceContext } from '@blaze-cms/ecommerce-context';
import { GET_PRODUCT_WITH_VARIANTS, GET_PRODUCT_VARIANTS_QUERY } from '../../services';
import ProductDetailsModal from './ProductDetailsModal';
import { getVariantData } from './helpers';
import getEan from '../../../../ecommerce-events-kp/utils/get-ean';
import getMappProperties from '../../../../ecommerce-events-kp/utils/get-mapp-properties';

const ProductDetailsWrapper = props => {
  const { parent } = props;

  // resets the component when the parent changes to make sure events are clean
  return <ProductDetails {...props} key={parent.itemId} />;
};

const ProductDetails = ({
  modifier,
  ebookMessage,
  ebookAccesInfo,
  gtmEventName,
  parent,
  enableGtmEventOnInitialLoad
}) => {
  const { itemId: parentId } = parent;
  const { currency: currencyState, variant } = useEcommerceContext(EcommerceContext);
  const currency = currencyState ? currencyState.toLowerCase() : currencyState;
  const [showModal, setShowModal] = useState(false);
  const [initialVariant] = useState(variant);

  const { data: { getPublishedProduct } = {}, error } = useQuery(GET_PRODUCT_WITH_VARIANTS, {
    variables: { id: parentId }
  });

  const { data: { productVariants } = {} } = useQuery(GET_PRODUCT_VARIANTS_QUERY, {
    variables: { id: parentId, entityIdentifier: 'publishedProduct', currency },
    skip: !gtmEventName || !currency
  });

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      try {
        if (
          !gtmEventName ||
          !currency ||
          !productVariants ||
          (!enableGtmEventOnInitialLoad && variant === initialVariant)
        )
          return; // don't send if gtmEventName is not set

        let variantToUse = variant;
        if (!variant && productVariants && productVariants.length) {
          // first load variant not set in context it will be the first one
          variantToUse = productVariants[0].variantId;
        }

        if (variantToUse && productVariants && getPublishedProduct) {
          const itemId = getEan(getPublishedProduct);
          const nameParts = getPublishedProduct.name.split('(');
          const name = nameParts[0].trim();
          const foundVariant = productVariants.find(({ variantId }) => variantId === variantToUse);
          const price = Number(foundVariant.price.slice(1));
          const gtmEventData = {
            event: gtmEventName,
            ecommerce: {
              currency,
              value: price,
              items: [
                {
                  item_id: itemId, // always user first variant isbn
                  item_name: name,
                  price,
                  item_variant: foundVariant.name,
                  quantity: 1,
                  mapp: getMappProperties(getPublishedProduct)
                }
              ]
            }
          };

          window.dataLayer.push(gtmEventData);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error in ProductDetails', e);
      }
    }, 500);
    return () => clearTimeout(debounceTimeout);
  }, [
    variant,
    productVariants,
    getPublishedProduct,
    gtmEventName,
    currency,
    initialVariant,
    enableGtmEventOnInitialLoad
  ]);

  if (error) return error.message;
  if (!getPublishedProduct) return null;
  const {
    uk_publication_date: ukDate,
    ukIsDisabled = false,
    us_publication_date: usDate,
    usIsDisabled = false,
    editionIssueNumber,
    formattedVariants
  } = getPublishedProduct;
  const variantData = getVariantData(variant, formattedVariants);
  if (!variantData) return null;

  const { attributes: { height, width, nb_pages: numberOfPages, isbn } = {} } = variantData;
  const isUkSelected = currency === 'gbp';
  const isEbook = variant === 'ebook';
  const isBundle = variant === 'bundle';
  const dateToUse = isUkSelected ? ukDate : usDate || '';
  const isDisabled = isUkSelected ? ukIsDisabled : usIsDisabled;
  const parsedDate = new Date(dateToUse).toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    timeZone: 'Europe/London'
  });

  const formatToUse = isEbook ? 'Epub' : `${width} x ${height}`;
  const hasEbook = isEbook || isBundle;

  if (isDisabled) {
    return (
      <>
        <style>{`
        .blaze-ecommerce-variants, .blaze-ecommerce-add-to-cart {
          display: none;
        }
      `}</style>
        <div className="kp-product-details__ebook-info">
          <p className="kp-product-details__ebook-info__text">Not available in your region.</p>
        </div>
      </>
    );
  }
  return (
    <div className="kp-product-details">
      {showModal && (
        <ProductDetailsModal modalContent={ebookAccesInfo} hideModal={() => setShowModal(false)} />
      )}
      {ebookMessage && isEbook && (
        <div className="kp-product-details__ebook-info">
          <p className="kp-product-details__ebook-info__text">{ebookMessage}</p>
        </div>
      )}
      <div className="kp-product-details__info">
        {hasEbook && (
          <div
            className="kp-product-details__modal-button"
            role="button"
            onClick={() => setShowModal(!showModal)}>
            <div className="kp-product-details__modal-button__icon">
              <i className="fas fa-circle-info" />
            </div>
            <span className="kp-product-details__modal-button__text">Ebook access info</span>
          </div>
        )}
        <div className="kp-product-details__info__properties">
          <div className="kp-product-details__info__properties__isbn">EAN: {isbn}</div>
          {!isEbook && <div>Edition: {editionIssueNumber}</div>}
          {dateToUse && (
            <div className="kp-product-details__info__properties__published_date">
              Published: <time dateTime={dateToUse}>{parsedDate}</time>
            </div>
          )}
          <div className="kp-product-details__info__properties__format">Format: {formatToUse}</div>
          <div className="kp-product-details__info__properties__pages">{numberOfPages} pages</div>
        </div>
      </div>
    </div>
  );
};

ProductDetails.propTypes = {
  parent: PropTypes.shape().isRequired,
  modifier: PropTypes.string,
  ebookMessage: PropTypes.string,
  ebookAccesInfo: PropTypes.string,
  gtmEventName: PropTypes.string,
  enableGtmEventOnInitialLoad: PropTypes.bool
};

ProductDetails.defaultProps = {
  modifier: '',
  ebookAccesInfo: '',
  ebookMessage: '',
  gtmEventName: '',
  enableGtmEventOnInitialLoad: false
};

export default ProductDetailsWrapper;
